import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';
import { AuthContext } from '../../context/AuthContext';

const API = `${API_BASE}`;

const toISO = (d) => {
  try { return new Date(d).toISOString(); } catch { return new Date().toISOString(); }
};

const fromISODate = (iso) => {
  try { return iso ? iso.split('T')[0] : ''; } catch { return ''; }
};

const JobDetail = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [jobs, setJobs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [monitoring, setMonitoring] = useState(false);

  const index = useMemo(() => jobs.findIndex(j => j.id === jobId), [jobs, jobId]);

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/jobs`, { headers: { 'Authorization': `Bearer ${token}` }});
        if (!res.ok) throw new Error(`failed to load jobs: ${res.status}`);
        const list = await res.json();
        const mine = Array.isArray(list) ? list.filter(j => (j.employer_id === user.id || j.employer_id === (user.id||'').toString())) : [];
        setJobs(mine);
      } catch (e) {
        console.warn(e);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/jobs/${jobId}`, { headers: { 'Authorization': `Bearer ${token}` }});
        if (res.ok) {
          const data = await res.json();
          setCurrent({
            ...data,
            // UI 用フォーマット
            start_date_input: fromISODate(data.start_date),
            end_date_input: fromISODate(data.end_date),
            publication_start_date_input: fromISODate(data.publication_start_date),
            publication_end_date_input: fromISODate(data.publication_end_date),
          });
        } else {
          // 詳細APIがない/404 の場合は一覧から推測
          const j = jobs.find(j => j.id === jobId);
          if (j) {
            setCurrent({
              ...j,
              start_date_input: fromISODate(j.start_date),
              end_date_input: fromISODate(j.end_date),
              publication_start_date_input: fromISODate(j.publication_start_date),
              publication_end_date_input: fromISODate(j.publication_end_date),
            });
          }
        }
      } catch (e) {
        const j = jobs.find(j => j.id === jobId);
        if (j) setCurrent({
          ...j,
          start_date_input: fromISODate(j.start_date),
          end_date_input: fromISODate(j.end_date),
          publication_start_date_input: fromISODate(j.publication_start_date),
          publication_end_date_input: fromISODate(j.publication_end_date),
        });
      }
    })();
  }, [jobId, jobs]);

  const gotoIndex = (i) => {
    if (i < 0 || i >= jobs.length) return;
    navigate(`/employer/job/${jobs[i].id}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrent(prev => ({ ...prev, [name]: value }));
  };

  // 更新保存: PUT がなければ POST で再投稿（フォールバック）
  const handleSave = async () => {
    if (!current) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      // まず PUT を試す
      const payload = {
        title: current.title,
        description: current.description,
        category: current.category || 'その他',
        hourly_rate: Number(current.hourly_rate || 0),
        duration_hours: Number(current.duration_hours || 8),
        start_date: toISO(current.start_date_input || current.start_date),
        end_date: toISO(current.end_date_input || current.end_date),
        location: current.location || current.address || '未設定',
        prefecture: current.prefecture || '未設定',
        city: current.city || '未設定',
        address: current.address || '未設定',
        latitude: current.latitude ?? null,
        longitude: current.longitude ?? null,
        requirements: Array.isArray(current.requirements) ? current.requirements : [],
        images: Array.isArray(current.images) ? current.images : [],
        videos: Array.isArray(current.videos) ? current.videos : [],
        thumbnail_image: current.thumbnail_image || (Array.isArray(current.images) && current.images[0]) || null,
        work_hours: current.work_hours || '',
        required_people: Number(current.required_people || 1),
        items_to_bring: Array.isArray(current.items_to_bring) ? current.items_to_bring : [],
        dress_code: current.dress_code || '',
        experience_required: !!current.experience_required,
        same_day_ok: !!current.same_day_ok,
        max_applicants: Number(current.max_applicants || 1),
        publication_start_date: current.publication_start_date_input ? toISO(current.publication_start_date_input) : null,
        publication_end_date: current.publication_end_date_input ? toISO(current.publication_end_date_input) : null,
        tags: Array.isArray(current.tags) ? current.tags : [],
      };

      let res = await fetch(`${API}/jobs/${current.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        // PUT がない/失敗 → 再投稿でフォールバック
        res = await fetch(`${API}/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`保存に失敗しました: ${res.status}`);
        toast.success('変更を反映するため、求人を再投稿しました');
      } else {
        toast.success('求人を更新しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleMonitorStart = async () => {
    try {
      setMonitoring(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/jobs/${jobId}/monitor-start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`monitor start failed: ${res.status}`);
      const data = await res.json();
      toast.success(`通知作成: リマインド${data?.created?.reminders || 0} / 遅刻${data?.created?.tardy || 0} / 未出勤${data?.created?.no_show || 0}`);
    } catch (e) {
      console.error(e);
      toast.error('モニター実行に失敗しました');
    } finally {
      setMonitoring(false);
    }
  };

  // 期限を1週間延長（最大+7日）。PUTがなければPOSTで再投稿。
  const handleExtendOneWeek = async () => {
    if (!current) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const baseEnd = current.publication_end_date_input || current.end_date_input || fromISODate(current.end_date);
      const end = new Date(baseEnd || Date.now());
      const extended = new Date(end.getTime() + 7 * 24 * 60 * 60 * 1000);

      const payload = {
        title: current.title,
        description: current.description,
        category: current.category || 'その他',
        hourly_rate: Number(current.hourly_rate || 0),
        duration_hours: Number(current.duration_hours || 8),
        start_date: toISO(current.start_date_input || current.start_date),
        end_date: toISO(current.end_date_input || current.end_date),
        location: current.location || current.address || '未設定',
        prefecture: current.prefecture || '未設定',
        city: current.city || '未設定',
        address: current.address || '未設定',
        latitude: current.latitude ?? null,
        longitude: current.longitude ?? null,
        requirements: Array.isArray(current.requirements) ? current.requirements : [],
        images: Array.isArray(current.images) ? current.images : [],
        videos: Array.isArray(current.videos) ? current.videos : [],
        thumbnail_image: current.thumbnail_image || (Array.isArray(current.images) && current.images[0]) || null,
        work_hours: current.work_hours || '',
        required_people: Number(current.required_people || 1),
        items_to_bring: Array.isArray(current.items_to_bring) ? current.items_to_bring : [],
        dress_code: current.dress_code || '',
        experience_required: !!current.experience_required,
        same_day_ok: !!current.same_day_ok,
        max_applicants: Number(current.max_applicants || 1),
        publication_start_date: current.publication_start_date_input ? toISO(current.publication_start_date_input) : null,
        publication_end_date: toISO(extended),
        tags: Array.isArray(current.tags) ? current.tags : [],
      };

      // まず PUT 試行（存在しない想定が高い）
      let res = await fetch(`${API}/jobs/${current.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        res = await fetch(`${API}/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`延長に失敗しました: ${res.status}`);
        toast.success('同じ内容で1週間延長して再投稿しました');
      } else {
        toast.success('掲載期限を1週間延長しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('延長に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !current) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/employer/job-management')} className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowUpLeftIcon className="w-5 h-5 mr-2" />
            求人一覧に戻る
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleMonitorStart} disabled={monitoring} className={`px-3 py-2 rounded ${monitoring ? 'bg-gray-200 text-gray-500' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}>
              当日モニター実行
            </button>
            <button disabled={index <= 0} onClick={() => gotoIndex(index - 1)} className={`p-2 rounded ${index <= 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}>
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <button disabled={index === jobs.length - 1 || index < 0} onClick={() => gotoIndex(index + 1)} className={`p-2 rounded ${(index === jobs.length - 1 || index < 0) ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}>
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 編集フォーム */}
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">求人内容の確認・編集</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">タイトル</label>
              <input className="w-full px-3 py-2 border rounded-lg" value={current.title||''} name="title" onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">カテゴリ</label>
              <input className="w-full px-3 py-2 border rounded-lg" value={current.category||''} name="category" onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">時給</label>
              <input type="number" className="w-full px-3 py-2 border rounded-lg" value={current.hourly_rate||''} name="hourly_rate" onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">勤務時間</label>
              <input className="w-full px-3 py-2 border rounded-lg" value={current.work_hours||''} name="work_hours" onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">掲載開始日</label>
              <input type="date" className="w-full px-3 py-2 border rounded-lg" value={current.publication_start_date_input||''} name="publication_start_date_input" onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">掲載終了日</label>
              <input type="date" className="w-full px-3 py-2 border rounded-lg" value={current.publication_end_date_input||''} name="publication_end_date_input" onChange={handleChange} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">仕事内容</label>
              <textarea className="w-full px-3 py-2 border rounded-lg" rows={4} value={current.description||''} name="description" onChange={handleChange} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={handleExtendOneWeek} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">1週間延長</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">変更を保存</button>
          </div>
        </div>

        {/* 他の求人へのクイックアクセス */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">他の求人</h2>
          {jobs.length === 0 ? (
            <p className="text-gray-500">求人がありません</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => navigate(`/employer/job/${j.id}`)} className={`text-left p-3 border rounded-lg hover:bg-gray-50 ${j.id===jobId? 'border-blue-300 bg-blue-50':'border-gray-200'}`}>
                  <div className="font-medium text-gray-900">{j.title}</div>
                  <div className="text-sm text-gray-600 line-clamp-2">{j.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
